import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';

/**
 * Which optional server features are configured on this instance.
 *
 * A self-hosted Paircraft runs with almost nothing configured — no Twilio, no
 * Stripe, no OpenAI — and the server correctly returns 503
 * FEATURE_NOT_CONFIGURED for those. Without this, the UI still rendered the
 * buttons, so the operator's first encounter with an unconfigured feature was
 * an error rather than its simple absence.
 *
 * Usage:
 *   const { isEnabled } = useFeatures();
 *   {isEnabled('sms') && <SendSmsButton />}
 *
 * Fails OPEN: if the endpoint cannot be reached (an older server that predates
 * /api/system/features, or a transient failure) every feature is treated as
 * enabled. Hiding real functionality because a metadata call failed would be a
 * worse bug than showing a button that 503s.
 */

export type FeatureKey =
  | 'email'
  | 'sms'
  | 'push'
  | 'payments'
  | 'paypal'
  | 'mediaStorage'
  | 'ai'
  | 'videoConferencing'
  | 'googleIntegrations'
  | 'lichess'
  | 'chesscom'
  | 'ratingsLookup';

interface FeatureInfo {
  enabled: boolean;
  /** 'local' when served by this instance, 'hub' when brokered by Paircraft. */
  via: 'local' | 'hub' | null;
}

interface FeaturesState {
  features: Partial<Record<FeatureKey, FeatureInfo>>;
  deployment: 'hosted' | 'self-hosted' | null;
  loading: boolean;
  /** True when the feature list could not be fetched and we are failing open. */
  degraded: boolean;
  isEnabled: (key: FeatureKey) => boolean;
  isBrokered: (key: FeatureKey) => boolean;
}

const FeaturesContext = createContext<FeaturesState | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<Partial<Record<FeatureKey, FeatureInfo>>>({});
  const [deployment, setDeployment] = useState<'hosted' | 'self-hosted' | null>(null);
  const [loading, setLoading] = useState(true);
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/system/features`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const body = await response.json();
        if (cancelled) return;

        setFeatures(body?.data?.features || {});
        setDeployment(body?.data?.deployment ?? null);
        setDegraded(false);
      } catch {
        if (cancelled) return;
        // Fail open — see the note above.
        setDegraded(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<FeaturesState>(() => {
    const isEnabled = (key: FeatureKey) => {
      // While loading, or when the lookup failed, assume available so nothing
      // the user relies on briefly vanishes and reappears.
      if (loading || degraded) return true;
      const info = features[key];
      return info ? info.enabled : true;
    };

    const isBrokered = (key: FeatureKey) => features[key]?.via === 'hub';

    return { features, deployment, loading, degraded, isEnabled, isBrokered };
  }, [features, deployment, loading, degraded]);

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

export function useFeatures(): FeaturesState {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
}

/**
 * Renders children only when `feature` is configured.
 *
 *   <FeatureGate feature="sms"><SendSmsButton /></FeatureGate>
 *
 * Pass `fallback` to explain the absence instead of hiding it silently —
 * worth doing where a user might otherwise go looking for the missing control.
 */
export function FeatureGate({
  feature,
  children,
  fallback = null,
}: {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { isEnabled } = useFeatures();
  return <>{isEnabled(feature) ? children : fallback}</>;
}
